'use client'

import { useOffMarketAuth } from './OffMarketAuthContext'
import { useOffMarketGate } from './OffMarketGate'
import { useEffect } from 'react'
import { useRouter } from 'next/navigation'
import type { ReactNode } from 'react'
import { Button } from '@/components/ui/button'

interface OffMarketDetailGateProps {
  children?: ReactNode
  /**
   * True when the SERVER withheld the listing entirely because no valid
   * `om_session` cookie was present — so `children` is empty by design and the
   * address never reached the browser. Once the visitor signs in we must ask
   * the server to re-render, otherwise they'd be authenticated and still
   * looking at an empty page (Steve 2026-08-24).
   */
  serverWithheld?: boolean
}

export function OffMarketDetailGate({ children, serverWithheld }: OffMarketDetailGateProps) {
  const { isAuthenticated, loading } = useOffMarketAuth()
  const { requestAccess } = useOffMarketGate()
  const router = useRouter()

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      requestAccess()
    }
  }, [loading, isAuthenticated, requestAccess])

  useEffect(() => {
    // Signed in, but the server sent no content — fetch the real page now.
    if (serverWithheld && !loading && isAuthenticated) {
      router.refresh()
    }
  }, [serverWithheld, loading, isAuthenticated, router])

  if (loading) {
    return (
      <div className="mx-auto max-w-[var(--container-content)] px-[var(--spacing-container)] py-24 text-center">
        <div className="w-6 h-6 border-2 border-foreground/20 border-t-foreground/60 rounded-full animate-spin mx-auto" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <div className="mx-auto max-w-[var(--container-content)] px-[var(--spacing-container)] py-24 text-center">
        <h2 className="font-display text-display-lg font-normal text-foreground mb-4">
          Off-Market Property
        </h2>
        <p className="text-body-sm text-foreground/70 mb-6">
          Sign in to view this exclusive off-market listing.
        </p>
        {/* Canonical Button, Figma 2198:34408 type=primary size=Regular (h36 / px24 /
            radius sm 2 / Body/bodyMdAlt 16-400 / theme/accent fill, black label). Was a
            hand-rolled black-filled 14px `btn-underline` box — Figma defines no motion on
            buttons, so the sliding underline is dropped with the migration. */}
        <Button type="button" onClick={requestAccess}>
          Sign In
        </Button>
      </div>
    )
  }

  // Authenticated, but this render came from a server-withheld page — hold the
  // spinner until router.refresh() delivers the real content.
  if (serverWithheld) {
    return (
      <div className="mx-auto max-w-[var(--container-content)] px-[var(--spacing-container)] py-24 text-center">
        <div className="w-6 h-6 border-2 border-foreground/20 border-t-foreground/60 rounded-full animate-spin mx-auto" />
      </div>
    )
  }

  return <>{children}</>
}
